'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import LoadingButton from '@mui/lab/LoadingButton'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import { useTranslations } from 'next-intl'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { useChallenge } from '~/app/sign/_hooks/use_challenge'
import client from '~/lib/client/client'
import { countries, Country } from '~/lib/country'

export interface Step2Data {
  first_name: string
  last_name: string
  username: string
  dob: string
  country: string
}

interface Step2Props {
  onBack: () => void
  onSubmit: (
    data: Step2Data,
  ) => Promise<{ field: keyof Step2Data; message: string } | null>
  loading: boolean
}

const Step2: React.FC<Step2Props> = ({ onBack, onSubmit, loading }) => {
  const t = useTranslations('auth.sign_up.form')
  const { challenge } = useChallenge()

  const usernameSchema = z
    .string()
    .min(2, { message: t('username.error.length_min') })
    .max(50, { message: t('username.error.length_max') })
    .regex(/^(?=.*[a-z])[a-z0-9_]+(\.[a-z0-9_]+)*$/, {
      message: t('username.error.format'),
    })

  const schema = z.object({
    first_name: z.string().min(1, { message: t('first_name.error') }),
    last_name: z.string().min(1, { message: t('last_name.error') }),
    username: usernameSchema,
    dob: z.coerce
      .date({ required_error: t('dob.error') })
      .max(new Date(new Date().setFullYear(new Date().getFullYear() - 18)), {
        message: t('dob.error_min_age', { age: 18 }),
      }),
    country: z.string().min(1, { message: t('country.error') }),
  })

  const {
    register,
    handleSubmit: handleFormSubmit,
    control,
    formState: { errors },
    setError,
  } = useForm<Step2Data>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: '',
      last_name: '',
      username: '',
      dob: '',
      country: '',
    },
  })

  const handleSubmit = async (data: Step2Data) => {
    const pow = await challenge()
    const [res, checkErr] = await client<{ usernameExists: boolean }>({
      path: '/api/auth/check',
      method: 'POST',
      body: { username: data.username, ...pow },
    })

    if (!checkErr && res?.usernameExists) {
      setError('username', { message: t('username.error.exists') })
      return
    }

    const err = await onSubmit(data)
    if (err) {
      setError(err.field, { message: err.message })
    }
  }

  return (
    <form onSubmit={handleFormSubmit(handleSubmit)}>
      <Grid container rowSpacing={2} columnSpacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label={t('first_name.label')}
            placeholder={t('first_name.placeholder')}
            sx={{ width: '100%' }}
            {...register('first_name')}
            error={!!errors.first_name}
            helperText={errors.first_name?.message?.toString()}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label={t('last_name.label')}
            placeholder={t('last_name.placeholder')}
            sx={{ width: '100%' }}
            {...register('last_name')}
            error={!!errors.last_name}
            helperText={errors.last_name?.message?.toString()}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TextField
            label={t('username.label')}
            placeholder={t('username.placeholder')}
            sx={{ width: '100%' }}
            {...register('username')}
            error={!!errors.username}
            helperText={errors.username?.message?.toString() || t('username.help')}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TextField
            label={t('dob.label')}
            type='date'
            sx={{ width: '100%' }}
            InputLabelProps={{ shrink: true }}
            {...register('dob')}
            error={!!errors.dob}
            helperText={errors.dob?.message?.toString()}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Controller
            name='country'
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={countries}
                getOptionLabel={(option: Country) => option.name}
                value={countries.find((c) => c.code === field.value) || null}
                onChange={(_, newValue) => {
                  field.onChange(newValue?.code || '')
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('country.label')}
                    placeholder={t('country.placeholder')}
                    error={!!errors.country}
                    helperText={errors.country?.message?.toString()}
                  />
                )}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 6 }}>
          <Button
            variant='outlined'
            fullWidth
            onClick={onBack}
            sx={{
              mt: 2,
              py: 1,
              borderColor: '#2a2a2a',
              color: '#f0f0f1',
              fontSize: '0.875rem',
              '&:hover': {
                borderColor: '#3a3a3a',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            {t('back')}
          </Button>
        </Grid>

        <Grid size={{ xs: 6 }}>
          <LoadingButton
            variant='contained'
            type='submit'
            fullWidth
            loading={loading}
            sx={{
              mt: 2,
              py: 1,
              background: loading
                ? '#1a1a1a'
                : 'linear-gradient(135deg, #27B1C6 0%, #59CDA1 100%)',
              boxShadow: loading ? 'none' : '0 2px 8px rgba(39, 177, 198, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2ed3ed 0%, #6df6c1 100%)',
                boxShadow: '0 4px 12px rgba(39, 177, 198, 0.4)',
              },
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
          >
            {t('submit')}
          </LoadingButton>
        </Grid>
      </Grid>
    </form>
  )
}

export default Step2
